import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Trophy, 
  TrendingUp, 
  AlertCircle, 
  Lightbulb,
  Clock,
  RotateCcw,
  MessageSquare,
  Target,
  Users,
  FileText
} from 'lucide-react';
import { InterviewFeedback as IInterviewFeedback } from '@/hooks/useInterviewSimulation';

interface InterviewFeedbackProps {
  feedback: IInterviewFeedback;
  duration: number;
  onNewSimulation: () => void;
}

const scoreCategories = [
  { key: 'comunicacao', label: 'Comunicação', icon: MessageSquare },
  { key: 'tecnico', label: 'Conhecimento Técnico', icon: Target },
  { key: 'estrutura', label: 'Estrutura', icon: FileText },
  { key: 'exemplos', label: 'Exemplos', icon: Users },
];

const getScoreColor = (score: number) => {
  if (score >= 80) return 'text-green-500';
  if (score >= 60) return 'text-yellow-500';
  return 'text-red-500';
};

const getScoreBg = (score: number) => {
  if (score >= 80) return 'bg-green-500';
  if (score >= 60) return 'bg-yellow-500';
  return 'bg-red-500';
};

const getScoreLabel = (score: number) => {
  if (score >= 90) return 'Excelente';
  if (score >= 80) return 'Muito Bom';
  if (score >= 70) return 'Bom';
  if (score >= 60) return 'Regular';
  return 'Precisa Melhorar';
};

export function InterviewFeedback({ feedback, duration, onNewSimulation }: InterviewFeedbackProps) {
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins} min ${secs}s`;
  };

  return (
    <div className="space-y-6">
      {/* Header with Overall Score */}
      <Card className="bg-gradient-to-br from-primary/10 to-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-1">Entrevista Concluída!</h2>
              <p className="text-muted-foreground flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Duração: {formatDuration(duration)}
              </p>
            </div>
            <div className="text-center">
              <div className={`text-5xl font-bold ${getScoreColor(feedback.score_overall)}`}>
                {feedback.score_overall}
              </div>
              <Badge className={`mt-1 ${getScoreBg(feedback.score_overall)} text-white`}>
                {getScoreLabel(feedback.score_overall)}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground">{feedback.resumo}</p>
        </CardContent>
      </Card>

      {/* Quick highlights */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="border-green-500/30 bg-green-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-green-600">
              <Trophy className="h-4 w-4" /> Principal ponto forte
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{feedback.pontos_fortes?.[0] ?? '—'}</p>
          </CardContent>
        </Card>
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-600">
              <AlertCircle className="h-4 w-4" /> Principal ponto fraco
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{feedback.areas_melhoria?.[0] ?? '—'}</p>
          </CardContent>
        </Card>
        <Card className="border-blue-500/30 bg-blue-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-blue-600">
              <Lightbulb className="h-4 w-4" /> Próxima ação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{feedback.dicas?.[0] ?? '—'}</p>
          </CardContent>
        </Card>
      </div>


      {/* Detailed Scores */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="h-5 w-5" />
            Pontuação por Categoria
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {scoreCategories.map(({ key, label, icon: Icon }) => {
            const score = feedback.scores[key as keyof typeof feedback.scores] || 0;
            return (
              <div key={key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                  <span className={`text-sm font-bold ${getScoreColor(score)}`}>
                    {score}/100
                  </span>
                </div>
                <Progress value={score} className="h-2" />
              </div>
            );
          })}
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Strengths */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-green-600">
              <Trophy className="h-5 w-5" />
              Pontos Fortes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {feedback.pontos_fortes.map((ponto, index) => (
                <li key={index} className="flex items-start gap-2">
                  <TrendingUp className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                  <span className="text-sm">{ponto}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Areas for Improvement */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-amber-600">
              <AlertCircle className="h-5 w-5" />
              Pontos Fracos / A Melhorar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {feedback.areas_melhoria.map((area, index) => (
                <li key={index} className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                  <span className="text-sm">{area}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Tips */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2 text-blue-600">
            <Lightbulb className="h-5 w-5" />
            Dicas para Melhorar
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {feedback.dicas.map((dica, index) => (
              <li key={index} className="flex items-start gap-2">
                <Lightbulb className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                <span className="text-sm">{dica}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Action Button */}
      <div className="flex justify-center">
        <Button size="lg" onClick={onNewSimulation} className="gap-2">
          <RotateCcw className="h-4 w-4" />
          Nova Simulação
        </Button>
      </div>
    </div>
  );
}
